import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, Text, TextInput, Pressable, ActivityIndicator, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { strings } from '@/constants/residents/strings';
import { UseLoginFormReturn } from './useLoginForm';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useResponsive } from '@/hooks/common/useResponsive';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { AppleSignInButton } from '@/components/auth/AppleSignInButton';
import { EmailInput } from '@/components/auth/inputs/EmailInput';
import { PasswordInput } from '@/components/auth/inputs/PasswordInput';
import { PrimaryButton } from '@/components/auth/buttons/PrimaryButton';
import { useToast } from '@/contexts/admin/ToastContext';
import { GoogleOAuthProvider } from '@/providers/GoogleOAuthProvider';
import { handleGoogleLoginError, handleAppleLoginError } from '@/utils/common/errorHandling';
import { useErrorHandler } from '@/hooks/common/useErrorHandler';

type Props = {
  state: UseLoginFormReturn;
  autoFocusEmail?: boolean;
  onForgotPassword?: () => void;
};

type LoginNavigationProp = StackNavigationProp<AuthStackParamList, 'Login'>;

export const LoginForm: React.FC<Props> = ({ state, autoFocusEmail, onForgotPassword }) => {
  const styles = useThemedStyles(makeStyles);
  const { colors } = useThemeColors();
  const [isLoginHovered, setLoginHovered] = useState(false);
  const { showError } = useToast();

  // Create a unified error handler that uses toast for web
  const toastErrorHandler = useCallback((message: string, _title?: string) => {
    // On web, we use toast instead of title
    showError(message);
  }, [showError]);

  // Set up unified error handling with toast for web
  useErrorHandler(toastErrorHandler);
  
  const {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    toggleShowPassword,
    isAdminMode,
    toggleAdminMode,
    isLoading,
    canSubmit,
    submit,
    formError,
    lastInvalidField,
  } = state;

  const { isMobile, isTablet, isDesktop } = useResponsive();
  const navigation = useNavigation<LoginNavigationProp>();

  const emailRef = useRef<TextInput>(null);
  const passwordRef = useRef<TextInput>(null);

  useEffect(() => {
    if (lastInvalidField === 'email') {
      emailRef.current?.focus?.();
    } else if (lastInvalidField === 'password') {
      passwordRef.current?.focus?.();
    }
  }, [lastInvalidField]);

  // Dynamic styles based on admin mode and responsive breakpoints
  const dynamicStyles = {
    formContainer: [
      styles.formContainer,
      isAdminMode ? styles.formContainerAdmin : styles.formContainerResident,
      isMobile && styles.formContainerMobile,
      isTablet && styles.formContainerTablet,
      isDesktop && styles.formContainerDesktop
    ],
    formContent: [
      styles.formContent,
      isMobile && styles.formContentMobile,
      isTablet && styles.formContentTablet,
      isDesktop && styles.formContentDesktop
    ],
    inputGroup: [
      styles.inputGroup,
      isMobile && styles.inputGroupMobile,
      isTablet && styles.inputGroupTablet,
      isDesktop && styles.inputGroupDesktop
    ],
    label: [
      styles.label,
      isAdminMode ? styles.labelAdmin : styles.labelResident
    ],
    input: [
      styles.input,
      isAdminMode ? styles.inputAdmin : styles.inputResident,
      isMobile && styles.inputMobile,
      isTablet && styles.inputTablet,
      isDesktop && styles.inputDesktop
    ],
    loginButton: [
      styles.loginButton,
      isAdminMode ? styles.loginButtonAdmin : styles.loginButtonResident,
      !canSubmit && styles.loginButtonDisabled,
      isMobile && styles.loginButtonMobile,
      isTablet && styles.loginButtonTablet,
      isDesktop && styles.loginButtonDesktop
    ],
    dividerContainer: [
      styles.dividerContainer,
      isTablet && styles.dividerContainerTablet,
    ],
    socialButtonsContainer: [
      styles.socialButtonsContainer,
      isTablet && styles.socialButtonsContainerTablet,
    ],
  };

  return (
    <GoogleOAuthProvider isAdmin={isAdminMode}>
      <View style={dynamicStyles.formContainer}>
        <View style={dynamicStyles.formContent}>
          {/* Email Input */}
          <View style={dynamicStyles.inputGroup}>
            <Text style={dynamicStyles.label}>Email</Text>
            <View style={styles.emailInputContainer}>
              <View style={styles.emailIcon}>
                <SVG_COMPONENTS.EMAIL_SOLID width={20} height={20} color={colors.text.muted} />
              </View>
              <EmailInput
                value={email}
                onChangeText={setEmail}
                placeholder="name@domain.com"
                inputRef={emailRef}
                autoFocus={autoFocusEmail}
                onSubmitEditing={() => passwordRef.current?.focus?.()}
                style={[dynamicStyles.input, styles.emailInput]}
              />
            </View>
          </View>

          {/* Password Input */}
          <View style={dynamicStyles.inputGroup}>
            <Text style={dynamicStyles.label}>Password</Text>
            <View style={styles.passwordContainer}>
              <PasswordInput
                value={password}
                onChangeText={setPassword}
                showPassword={showPassword}
                toggleShowPassword={toggleShowPassword}
                inputRef={passwordRef}
                onSubmitEditing={submit}
                style={[dynamicStyles.input, styles.passwordInput]}
              />
            </View>
          </View>

          {/* Error Message */}
          {!!formError && (
            <View accessibilityLiveRegion="polite" style={styles.errorContainer}>
              <Text style={styles.errorText}>{formError}</Text>
            </View>
          )}

          {/* Admin Mode Toggle - Two-Segment Control */}
          <View style={styles.adminToggleContainer}>
            <View style={[styles.segmentedControl, isMobile && styles.segmentedControlMobile]}>
              <TouchableOpacity 
                style={[
                  styles.segment,
                  styles.segmentLeft,
                  !isAdminMode && styles.segmentActive
                ]}
                onPress={() => !isAdminMode || toggleAdminMode()}
                activeOpacity={0.7}
              >
                <Text style={[
                  styles.segmentText,
                  !isAdminMode && styles.segmentTextActive,
                  isMobile && styles.segmentTextMobile
                ]}>
                  Resident
                </Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[
                  styles.segment,
                  styles.segmentRight,
                  isAdminMode && styles.segmentActive
                ]}
                onPress={() => isAdminMode || toggleAdminMode()}
                activeOpacity={0.7}
              >
                <Text style={[
                  styles.segmentText,
                  isAdminMode && styles.segmentTextActive,
                  isMobile && styles.segmentTextMobile
                ]}>
                  Admin
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Sign In Button */}
          <PrimaryButton
            title="Sign in"
            onPress={submit}
            disabled={!canSubmit}
            loading={isLoading}
            style={[...dynamicStyles.loginButton, Platform.OS === 'web' && isLoginHovered && canSubmit && styles.loginButtonHover]}
            onHoverIn={() => setLoginHovered(true) as any}
            onHoverOut={() => setLoginHovered(false) as any}
          />

          {/* TEMPORARILY DISABLED - 3rd party login buttons commented out for App Store review */}
          {/* Divider with "or" text */}
          {/* <View style={dynamicStyles.dividerContainer}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>or continue with</Text>
            <View style={styles.dividerLine} />
          </View> */}

          {/* Social Sign-In Buttons Container */}
          {/* <View style={dynamicStyles.socialButtonsContainer}>
            {/* Google Sign-In Button - Only show in production/staging */}
            {/* <GoogleSignInButton
              onSuccess={async (credential: string) => {
                try {
                  await state.googleLogin(credential);
                } catch (error: any) {
                  // Error is already set in form state and handled by unified error handler
                  handleGoogleLoginError(error);
                }
              }}
              onError={(error: string) => {
                handleGoogleLoginError(error || 'Google login failure');
              }}
              isAdmin={isAdminMode}
              disabled={isLoading}
            /> */}

            {/* Apple Sign-In Button - Only show in production/staging */}
            {/* <AppleSignInButton
              onSuccess={async (identityToken: string, user) => {
                try {
                  await state.appleLogin(identityToken, user);
                } catch (error: any) {
                  // Error is already set in form state and handled by unified error handler
                  handleAppleLoginError(error);
                }
              }}
              onError={(error: string) => {
                handleAppleLoginError(error || 'Apple login failure');
              }}
              isAdmin={isAdminMode}
              disabled={isLoading}
            /> */}
          {/* </View> */}
        </View>
      </View>
    </GoogleOAuthProvider>
  );
};

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  formContainer: {
    width: '100%',
  },
  // Admin-specific form container
  formContainerAdmin: {
    // Admin-specific styling can go here
  },
  // Resident-specific form container
  formContainerResident: {
    // Resident-specific styling can go here
  },
  // Mobile: Reduced padding
  formContainerMobile: {
    paddingHorizontal: 0,
  },
  // Tablet: Centered with max width
  formContainerTablet: {
    paddingHorizontal: 24,
    maxWidth: 480,
    alignSelf: 'center',
  },
  // Desktop: Original padding
  formContainerDesktop: {
    paddingHorizontal: 0,
  },
  adminToggleContainer: {
    alignItems: 'center',
    // marginVertical: 20,
  },
  segmentedControl: {
    flexDirection: 'row',
    backgroundColor: semantic.background.tertiary,
    borderRadius: 6,
    padding: 3,
    width: 200, // Smaller width
    borderWidth: 1,
    borderColor: semantic.border.secondary,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
      },
    }),
  },
  segmentedControlMobile: {
    width: 180, // Even smaller on mobile
  },
  segment: {
    flex: 1,
    paddingVertical: 6, // Smaller padding
    paddingHorizontal: 12,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        transition: 'all 0.2s ease-in-out' as any,
        userSelect: 'none' as any,
      },
    }),
  },
  segmentLeft: {
    marginRight: 1.5,
  },
  segmentRight: {
    marginLeft: 1.5,
  },
  segmentActive: {
    backgroundColor: semantic.interactive.secondary, // resioPink — identical in both themes
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 1,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)' as any,
      },
    }),
  },
  segmentText: {
    fontSize: 13, // Smaller font
    fontWeight: '500',
    color: colors.text.muted,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
    ...Platform.select({
      web: {
        transition: 'color 0.2s ease-in-out' as any,
        userSelect: 'none' as any,
      },
    }),
  },
  segmentTextActive: {
    color: '#FFFFFF', // on-fill: active segment is resioPink in both themes
    fontWeight: '600',
  },
  segmentTextMobile: {
    fontSize: 12, // Even smaller on mobile
  },
  formContent: {
    width: '100%',
  },
  // Mobile: Reduced spacing
  formContentMobile: {
    gap: 16,
  },
  // Tablet: Medium spacing
  formContentTablet: {
    gap: 20,
  },
  // Desktop: Original spacing
  formContentDesktop: {
    gap: 24,
  },
  inputGroup: {
    // marginBottom: 12,
  },
  // Mobile: Reduced margin
  inputGroupMobile: {
    marginBottom: 16,
  },
  // Tablet: Medium margin
  inputGroupTablet: {
    marginBottom: 20,
  },
  // Desktop: Original margin
  inputGroupDesktop: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  // Admin-specific label
  labelAdmin: {
    color: colors.text.primary,
  },
  // Resident-specific label
  labelResident: {
    color: colors.text.primary,
  },
  emailInputContainer: {
    position: 'relative',
    width: '100%',
  },
  emailIcon: {
    position: 'absolute',
    left: 16,
    top: 0,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  emailInput: {
    paddingLeft: 48, // Make room for icon
  },
  input: {
    height: 48,
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 16,
    fontSize: 16,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
    ...Platform.select({
      web: {
        outlineStyle: 'none' as any,
      },
    }),
  },
  // Admin-specific input
  inputAdmin: {
    backgroundColor: semantic.background.input,
    borderColor: semantic.border.secondary,
    color: colors.text.primary,
  },
  // Resident-specific input
  inputResident: {
    backgroundColor: semantic.background.input,
    borderColor: semantic.border.secondary,
    color: colors.text.primary,
  },
  // Mobile: Slightly smaller input
  inputMobile: {
    height: 44,
    fontSize: 16,
  },
  // Tablet: Medium size input
  inputTablet: {
    height: 46,
    fontSize: 16,
  },
  // Desktop: Original size
  inputDesktop: {
    height: 48,
    fontSize: 16,
  },
  passwordContainer: {
    position: 'relative',
    width: '100%',
  },
  passwordInput: {
    paddingRight: 50,
    width: '100%',
  },
  eyeButton: {
    position: 'absolute',
    right: 16,
    top: 0,
    height: 48,
    width: 40,
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
      },
    }),
  },
  loginButton: {
    height: 48,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'all 0.2s ease-in-out' as any,
      },
    }),
  },
  // Admin-specific login button
  loginButtonAdmin: {
    backgroundColor: semantic.interactive.secondary,
  },
  // Resident-specific login button
  loginButtonResident: {
    backgroundColor: semantic.interactive.secondary,
  },
  loginButtonHover: {
    opacity: 0.85,
  },
  loginButtonDisabled: {
    // NOT semantic.interactive.disabled: that is #BBBBBB in light, which would
    // drop the white label to ~1.9:1. Fixed mid-gray keeps the label legible
    // in both themes.
    backgroundColor: '#6B7280',
    opacity: 0.6,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
        '&:hover': {
          backgroundColor: '#6B7280' as any,
        },
      },
    }),
  },
  // Mobile: Full width button
  loginButtonMobile: {
    width: '100%',
  },
  // Tablet: Full width button with adjusted height
  loginButtonTablet: {
    width: '100%',
    height: 46,
  },
  // Desktop: Full width button
  loginButtonDesktop: {
    width: '100%',
  },
  loginButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  errorContainer: {
    marginBottom: 16,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: semantic.background.tertiary,
    borderColor: semantic.status.error,
    borderWidth: 1,
    borderRadius: 6,
  },
  errorText: {
    color: semantic.status.error,
    fontSize: 14,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  // Divider styles
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
  },
  dividerContainerTablet: {
    marginVertical: 24,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: semantic.border.secondary,
  },
  dividerText: {
    color: colors.text.muted,
    fontSize: 13,
    fontWeight: '500',
    paddingHorizontal: 16,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  // Social buttons container
  socialButtonsContainer: {
    gap: 12,
  },
  socialButtonsContainerTablet: {
    gap: 14,
  },
});
